/**
 * GMA-1566 (follow-up to GMA-1504): show Silver Shield occupancy as sessions.
 *
 * Instead of a stream of individual occupied/vacant (and bed-in/out, couch-in/out)
 * blips, a single stay reads as ONE entry with a start time, an end time, and a
 * duration — the way Pontosense presents it. Every other event type (falls,
 * doors, offline, etc.) keeps the GMA-1504 same-window grouping.
 *
 * Display-only: the underlying raw events are preserved on each entry (`events`)
 * so the raw detail is still available, and stored data is never changed.
 */

import { groupStaffEvents } from "./groupStaffEvents";

// Occupancy "stay" pairs: a start transition closes when its matching end
// transition arrives for the same sensor. Anything not in this map is left to
// the same-window grouping (it is not a stay with a duration).
const SESSION_PAIRS = [
  { start: "sensor.detection.occupied", end: "sensor.detection.vacant", label: "Room occupied" },
  { start: "sensor.detection.bed_in", end: "sensor.detection.bed_out", label: "In bed" },
  { start: "sensor.detection.couch_in", end: "sensor.detection.couch_out", label: "On couch" },
];
const START_TYPES = new Map(SESSION_PAIRS.map((p) => [p.start, p]));
const END_TYPES = new Map(SESSION_PAIRS.map((p) => [p.end, p]));

function timeMs(eventAt) {
  const t = new Date(eventAt).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * Build the staff-pane timeline: occupancy stays as sessions, everything else
 * as same-window groups, merged newest-first.
 *
 * @param {Array<object>} events raw events ({ id, eventType, eventLabel, eventAt, sensorSn }).
 * @returns {Array<object>} newest-first entries. A session entry:
 *   { kind:"session", sessionType, label, sensorSn, startAt, endAt|null,
 *     ongoing:boolean, durationMs:number|null, events:[start(,end)] }.
 *   A grouped entry is the groupStaffEvents shape plus { kind:"group" }.
 */
export function buildStaffTimeline(events) {
  if (!Array.isArray(events) || events.length === 0) return [];

  // Pair transitions in chronological order (oldest -> newest).
  const chrono = [...events].sort(
    (a, b) => (timeMs(a?.eventAt) ?? 0) - (timeMs(b?.eventAt) ?? 0),
  );

  const sessions = [];
  const nonSession = [];
  // key = `${startType}:${sensorSn}` -> the currently-open session entry.
  const open = new Map();

  for (const ev of chrono) {
    const type = ev?.eventType;
    const sn = ev?.sensorSn || "";
    if (START_TYPES.has(type)) {
      const pair = START_TYPES.get(type);
      const entry = {
        kind: "session",
        sessionType: pair.start,
        label: pair.label,
        sensorSn: ev.sensorSn || null,
        startAt: ev.eventAt,
        endAt: null,
        ongoing: true,
        durationMs: null,
        events: [ev],
      };
      sessions.push(entry);
      // A second start before an end leaves the prior session open (ongoing);
      // the latest start becomes the one a matching end will close.
      open.set(`${pair.start}:${sn}`, entry);
    } else if (END_TYPES.has(type)) {
      const pair = END_TYPES.get(type);
      const key = `${pair.start}:${sn}`;
      const entry = open.get(key);
      if (entry) {
        entry.endAt = ev.eventAt;
        entry.ongoing = false;
        entry.durationMs = Math.max(
          0,
          (timeMs(ev.eventAt) ?? 0) - (timeMs(entry.startAt) ?? 0),
        );
        entry.events.push(ev);
        open.delete(key);
      } else {
        // End with no matching start (e.g. history window starts mid-stay):
        // fall back to a normal event row rather than inventing a session.
        nonSession.push(ev);
      }
    } else {
      nonSession.push(ev);
    }
  }

  const groups = groupStaffEvents(nonSession).map((g) => ({ kind: "group", ...g }));

  // Merge sessions + groups, sorted newest-first by their most recent moment
  // (a session anchors on its end if closed, else its start).
  const anchorOf = (e) =>
    e.kind === "session"
      ? timeMs(e.endAt) ?? timeMs(e.startAt) ?? 0
      : timeMs(e.eventAt) ?? 0;

  return [...sessions, ...groups].sort((a, b) => anchorOf(b) - anchorOf(a));
}

export default buildStaffTimeline;
